import { differenceInCalendarYears, format, formatDistanceToNowStrict, isToday, isYesterday } from "date-fns";

/**
 * Formats a date as a short, human-friendly relative string for recent
 * dates, falling back to an absolute format for older dates.
 *
 * Examples:
 *  - "just now"
 *  - "2m ago"
 *  - "3h ago"
 *  - "Yesterday"
 *  - "5 days ago"
 *  - "3 weeks ago"
 *  - "Feb 12" (same year, older than ~30 days)
 *  - "Feb 2025" (different year)
 */
export function formatRelativeDate(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = diffMs / 1000;
  const diffMinutes = diffSeconds / 60;
  const diffHours = diffMinutes / 60;
  const diffDays = diffHours / 24;

  if (diffSeconds < 45) {
    return "just now";
  }

  if (diffMinutes < 60) {
    return formatDistanceToNowStrict(date, { unit: "minute", addSuffix: true });
  }

  if (diffHours < 24) {
    return formatDistanceToNowStrict(date, { unit: "hour", addSuffix: true });
  }

  if (isYesterday(date)) {
    return "Yesterday";
  }

  if (diffDays < 7 && diffDays < 30) {
    return formatDistanceToNowStrict(date, { unit: "day", addSuffix: true });
  }

  if (differenceInCalendarYears(now, date) === 0) {
    return format(date, "MMM d");
  }

  return format(date, "MMM yyyy");
}

/**
 * Formats a date as a full, absolute date and time string, suitable for
 * use in tooltips alongside a relative date.
 *
 * Example: "12 Feb 2025, 14:30"
 */
export function formatAbsoluteDateTime(date: Date): string {
  if (isToday(date)) {
    return `Today at ${format(date, "HH:mm")}`;
  }

  return format(date, "d MMM yyyy, HH:mm");
}
